import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { useIdentityReveal } from '../../contexts/IdentityRevealContext';
import { useRealtimeResumes } from '../../hooks/useRealtimeResumes';
import { useRealtimeJobs } from '../../hooks/useRealtimeJobs';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { isMyItem } from '../../utils/ownership';
import { timeAgo } from '../../utils/timeAgo';
import {
  BULK_UPLOAD_MAX_FILES,
  DIRECT_UPLOAD_MAX_FILES,
  MAX_FILE_SIZE_BYTES,
  MAX_PDF_PAGES,
  issuesToTooltip,
  toMb,
  validateResumeFiles,
} from '../../utils/uploadPolicy';
import './ResumesListPage.css';

const IconSearch = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
);

const IconUpload = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
);

export const ResumesListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { org } = useTenant();
  const { user } = useAuth();
  const { revealIdentity } = useIdentityReveal();
  const authFetch = useAuthFetch();
  const { resumes, loading: resumesLoading } = useRealtimeResumes(org?.orgId);
  const { jobs } = useRealtimeJobs(org?.orgId);

  const [search, setSearch] = useState('');
  const [hidePlaced, setHidePlaced] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState<'mine' | 'all'>('mine');
  const myUid = user?.uid;
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [uploadStatusTooltip, setUploadStatusTooltip] = useState<string>('');
  const prewarmFired = useRef(false);
  const fromGuidedEntry = searchParams.get('entry') === 'guided';

  const clearGuidedEntry = useCallback(() => {
    if (!fromGuidedEntry) return;
    navigate('/talent', { replace: true });
  }, [fromGuidedEntry, navigate]);

  useEffect(() => {
    if (!uploading) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [uploading]);

  const fallbackAlias = useCallback((resumeId: string) => `Candidate ${(resumeId || 'unknown').slice(0, 8).toUpperCase()}`, []);

  const getResumeDisplayName = useCallback((resume: { resumeId: string; filename: string; candidateAlias?: string; displayName?: string }) => {
    if (revealIdentity && resume.filename) return resume.filename;
    return resume.displayName || resume.candidateAlias || fallbackAlias(resume.resumeId);
  }, [fallbackAlias, revealIdentity]);

  const jdTitles = useMemo(() => {
    const map: Record<string, string> = {};
    jobs.forEach(j => { map[j.jdId] = j.title || 'Untitled'; });
    return map;
  }, [jobs]);

  const filtered = useMemo(() => {
    let result = resumes;
    if (ownerFilter === 'mine') result = result.filter(r => isMyItem(r.uploadedBy, myUid));
    if (hidePlaced) result = result.filter(r => !r.placedInJobId);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(r => {
        const display = getResumeDisplayName(r).toLowerCase();
        const original = (r.filename || '').toLowerCase();
        return display.includes(q) || (revealIdentity && original.includes(q));
      });
    }
    return result;
  }, [resumes, search, hidePlaced, ownerFilter, myUid, getResumeDisplayName, revealIdentity]);

  const myResumesCount = useMemo(
    () => resumes.filter(r => isMyItem(r.uploadedBy, myUid)).length,
    [resumes, myUid]
  );

  const getRejectedDetail = useCallback((resume: { status: string; rejectionReason?: string; rejectionDetail?: string; failureReason?: string; error?: string; pageCount?: number; fileSize: number }) => {
    if (resume.rejectionReason === 'too_many_pages' || typeof resume.pageCount === 'number') {
      const pages = typeof resume.pageCount === 'number' ? resume.pageCount : undefined;
      return pages
        ? `Uploaded: ${pages} pages. Max allowed: ${MAX_PDF_PAGES} pages.`
        : `Resume exceeds max allowed pages (${MAX_PDF_PAGES}).`;
    }
    if (resume.rejectionReason === 'file_too_large') {
      return `Uploaded: ${toMb(resume.fileSize)} MB. Max allowed: ${toMb(MAX_FILE_SIZE_BYTES)} MB.`;
    }
    if (resume.rejectionDetail) return resume.rejectionDetail;
    if (resume.failureReason) return resume.failureReason;
    if (resume.error) return resume.error;

    switch (resume.status) {
      case 'upload_failed':
        return 'Upload to storage failed before processing. Please retry.';
      case 'failed':
        return 'Processing failed after upload. Please retry.';
      case 'rejected':
        return 'Rejected by upload validation policy.';
      case 'unsupported_file_type':
        return 'Unsupported file type. Allowed: PDF or DOCX.';
      case 'file_too_large':
        return `Uploaded: ${toMb(resume.fileSize)} MB. Max allowed: ${toMb(MAX_FILE_SIZE_BYTES)} MB.`;
      case 'too_many_pages':
        return `Resume exceeds max allowed pages (${MAX_PDF_PAGES}).`;
      default:
        return 'Upload failed due to policy or processing limits.';
    }
  }, []);

  const shouldShowFailureDetail = useCallback((status: string) => (
    status === 'rejected_oversized'
    || status === 'failed'
    || status === 'upload_failed'
    || status === 'rejected'
    || status === 'unsupported_file_type'
    || status === 'file_too_large'
    || status === 'too_many_pages'
  ), []);

  const sha256Hex = useCallback(async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
  }, []);

  const uploadFailureMessage = useCallback(async (res: Response, file?: File): Promise<string> => {
    const fallback = file
      ? `${file.name}: upload failed (HTTP ${res.status})`
      : `Upload failed (HTTP ${res.status})`;
    try {
      const body = await res.json();
      const detail = body?.detail;
      if (typeof detail === 'string' && detail.trim()) return detail;
      if (!detail || typeof detail !== 'object') return fallback;

      const reason = detail.reason ? `reason=${detail.reason}` : '';
      const fileName = detail.file?.filename || file?.name || 'file';
      const fileType = detail.file?.contentType || file?.type || 'unknown';
      const sizeBytes = Number(detail.file?.sizeBytes ?? file?.size ?? 0);
      const maxBytes = Number(detail.limits?.maxFileSizeBytes ?? 0);
      const accepted = Array.isArray(detail.limits?.acceptedExtensions)
        ? detail.limits.acceptedExtensions.join(', ')
        : '.pdf, .docx';
      const message = detail.message || 'Upload failed';
      const sizePart = sizeBytes > 0 ? `uploaded=${toMb(sizeBytes)} MB` : '';
      const maxPart = maxBytes > 0 ? `max=${toMb(maxBytes)} MB` : '';
      const extras = [reason, `type=${fileType}`, sizePart, maxPart, `allowed=${accepted}`]
        .filter(Boolean)
        .join(', ');
      return `${fileName}: ${message}${extras ? ` (${extras})` : ''}`;
    } catch {
      return fallback;
    }
  }, []);

  const uploadFiles = useCallback(async (files: File[]) => {
    const { validFiles, issues } = await validateResumeFiles(files);
    if (validFiles.length === 0 && issues.length > 0) {
      setUploadStatus(`No files were accepted. ${issues.length} file(s) failed validation.`);
      setUploadStatusTooltip(issuesToTooltip(issues));
      setTimeout(() => setUploadStatus(null), 8000);
      return;
    }

    if (validFiles.length > BULK_UPLOAD_MAX_FILES) {
      setUploadStatus(
        `Bulk upload is capped at ${BULK_UPLOAD_MAX_FILES} files. ` +
        `For larger imports, ask an admin to use Migration Center (Admin → Migration Center).`
      );
      setUploadStatusTooltip('Limit exceeded: use Migration Center for 201-2000 files.');
      setTimeout(() => setUploadStatus(null), 8000);
      return;
    }

    setUploading(true);
    setUploadStatus(null);
    setUploadStatusTooltip('');

    if (validFiles.length > DIRECT_UPLOAD_MAX_FILES) {
      try {
        const filesPayload = await Promise.all(
          validFiles.map(async (file) => ({
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
            hash: await sha256Hex(file),
            size: file.size,
          }))
        );

        const initRes = await authFetch('/api/batch-upload-init', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ files: filesPayload, jdId: null }),
        });
        if (!initRes.ok) {
          const msg = await uploadFailureMessage(initRes);
          throw new Error(msg);
        }
        const initData = await initRes.json();
        const uploads: Array<{ clientIndex: number; resumeId: string; uploadUrl: string; contentType: string }> = initData.uploads ?? [];
        const duplicates: Array<{ resumeId: string; filename?: string }> = initData.duplicates ?? [];

        let fail = 0;
        const successResumeIds: string[] = duplicates.map(d => d.resumeId).filter(Boolean);
        const serverFailures: string[] = [];
        const CONCURRENCY = 4;
        for (let batchStart = 0; batchStart < uploads.length; batchStart += CONCURRENCY) {
          const chunk = uploads.slice(batchStart, batchStart + CONCURRENCY);
          const results = await Promise.allSettled(
            chunk.map(async (u) => {
              const file = validFiles[u.clientIndex];
              if (!file) throw new Error('missing file mapping');
              const putRes = await fetch(u.uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': u.contentType || file.type || 'application/octet-stream' },
                body: file,
              });
              if (!putRes.ok) throw new Error(`${file.name}: signed upload failed (HTTP ${putRes.status})`);
              successResumeIds.push(u.resumeId);
            })
          );
          for (const r of results) {
            if (r.status === 'rejected') {
              fail++;
              serverFailures.push(r.reason instanceof Error ? r.reason.message : String(r.reason));
            }
          }
        }

        if (successResumeIds.length === 0 && uploads.length > 0) {
          throw new Error('All file uploads failed — no files reached storage.');
        }

        if (uploads.length > 0) {
          const startRes = await authFetch(`/api/batch-start/${initData.batchId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uploadedResumeIds: successResumeIds }),
          });
          if (!startRes.ok) {
            const msg = await uploadFailureMessage(startRes);
            throw new Error(msg);
          }
        }

        setUploading(false);
        prewarmFired.current = false;
        window.dispatchEvent(new Event('usage:invalidate'));
        if (fromGuidedEntry && successResumeIds.length > 0) {
          if (successResumeIds.length === 1) {
            navigate(`/talent/${successResumeIds[0]}`, { replace: true });
            return;
          }
          clearGuidedEntry();
        }
        setUploadStatus(
          fail > 0 || issues.length > 0
            ? `Batch queued (${successResumeIds.length} accepted, ${duplicates.length} duplicate${duplicates.length !== 1 ? 's' : ''}). ${fail + issues.length} file${(fail + issues.length) !== 1 ? 's' : ''} failed validation/upload.`
            : `Batch upload queued. ${duplicates.length} duplicate${duplicates.length !== 1 ? 's were' : ' was'} reused.`
        );
        const tips = [
          issues.length > 0 ? issuesToTooltip(issues) : '',
          serverFailures.length > 0 ? serverFailures.join('\n') : '',
        ].filter(Boolean).join('\n');
        setUploadStatusTooltip(tips);
        setTimeout(() => setUploadStatus(null), 6000);
        return;
      } catch (e) {
        setUploading(false);
        prewarmFired.current = false;
        setUploadStatus(e instanceof Error ? e.message : 'Batch upload failed. Please retry.');
        setTimeout(() => setUploadStatus(null), 4000);
        return;
      }
    }

    let ok = 0;
    let fail = 0;
    const successResumeIds: string[] = [];
    const serverFailures: string[] = [];

    const CONCURRENCY = 4;
    for (let batchStart = 0; batchStart < validFiles.length; batchStart += CONCURRENCY) {
      const batch = validFiles.slice(batchStart, batchStart + CONCURRENCY);
      const results = await Promise.allSettled(
        batch.map(async (file) => {
          const formData = new FormData();
          formData.append('resume', file);
          const res = await authFetch('/api/upload-resume', { method: 'POST', body: formData });
          if (!res.ok) throw new Error(await uploadFailureMessage(res, file));
          const data = await res.json().catch(() => null);
          return typeof data?.resumeId === 'string' ? data.resumeId : null;
        })
      );
      for (const r of results) {
        if (r.status === 'fulfilled') {
          ok++;
          if (r.value) successResumeIds.push(r.value);
        }
        else {
          fail++;
          serverFailures.push(r.reason instanceof Error ? r.reason.message : String(r.reason));
        }
      }
    }

    setUploading(false);
    prewarmFired.current = false;
    if (ok > 0) window.dispatchEvent(new Event('usage:invalidate'));
    if (fromGuidedEntry && successResumeIds.length > 0) {
      if (successResumeIds.length === 1) {
        navigate(`/talent/${successResumeIds[0]}`, { replace: true });
        return;
      }
      clearGuidedEntry();
    }
    setUploadStatus(`Uploaded ${ok} file${ok !== 1 ? 's' : ''}${fail + issues.length > 0 ? `, ${fail + issues.length} failed` : ''}`);
    setUploadStatusTooltip([
      issues.length > 0 ? issuesToTooltip(issues) : '',
      serverFailures.length > 0 ? serverFailures.join('\n') : '',
    ].filter(Boolean).join('\n'));
    setTimeout(() => setUploadStatus(null), 4000);
  }, [authFetch, clearGuidedEntry, fromGuidedEntry, navigate, sha256Hex, uploadFailureMessage]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    uploadFiles(files);
  }, [uploadFiles]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      if (!prewarmFired.current) {
        prewarmFired.current = true;
        authFetch('/api/prewarm-enrichment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ count: Math.min(e.target.files.length, 100) }),
        }).catch(() => {});
      }
      uploadFiles(Array.from(e.target.files));
      e.target.value = '';
    }
  }, [uploadFiles, authFetch]);

  return (
    <div className="v2-page">
      <div className="v2-page-header">
        <h1>Talent Pool</h1>
        <p>Browse candidates, then open a profile to review details</p>
      </div>

      <div
        className="v2-card"
        style={{
          marginBottom: 'var(--space-4)',
          border: dragActive ? '2px dashed var(--color-primary)' : undefined,
          background: dragActive ? 'var(--color-primary-alpha)' : undefined,
        }}
        onDragOver={e => { e.preventDefault(); setDragActive(true); }}
        onDragEnter={() => { setDragActive(true); if (!prewarmFired.current) { prewarmFired.current = true; authFetch('/api/prewarm-enrichment', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ count: 5 }) }).catch(() => {}); } }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
      >
        <div className="v2-card-body" style={{ textAlign: 'center', padding: 'var(--space-6)' }}>
          <input type="file" multiple accept=".pdf,.docx" onChange={handleFileInput} id="talent-upload" style={{ display: 'none' }} />
          <label htmlFor="talent-upload" style={{ cursor: 'pointer', display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-2)' }}>
            <IconUpload />
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
              {uploading ? 'Uploading...' : 'Drop resumes here or click to upload'}
            </span>
            {uploading
              ? <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-warning, #f59e0b)' }}>Please keep this tab open until upload completes</span>
              : <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>PDF or DOCX — {`10+ files auto-switches to batch mode`}</span>}
          </label>
          {uploadStatus && (
            <div
              title={uploadStatusTooltip || undefined}
              style={{ marginTop: 'var(--space-3)', fontSize: 'var(--font-size-sm)', color: 'var(--color-success)', whiteSpace: 'pre-wrap' }}
            >
              {uploadStatus}
            </div>
          )}
        </div>
      </div>

      <div className="v2-filter-bar">
        <div className="v2-search-wrapper resumes-list-search-wrapper">
          <IconSearch />
          <input
            className="v2-search-input"
            placeholder="Search resumes..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          {filtered.length} resume{filtered.length !== 1 ? 's' : ''}
        </span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          <span>Show:</span>
          <select
            value={ownerFilter}
            onChange={e => setOwnerFilter(e.target.value as 'mine' | 'all')}
            aria-label="Filter resumes by owner"
            style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 'var(--font-size-xs)' }}
          >
            <option value="mine">Mine ({myResumesCount})</option>
            <option value="all">All ({resumes.length})</option>
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', cursor: 'pointer' }}>
          <input type="checkbox" checked={hidePlaced} onChange={() => setHidePlaced(p => !p)} />
          Hide placed
        </label>
      </div>

      <div className="v2-card">
        <div className="v2-card-body-flush">
          {resumesLoading ? (
            <div className="v2-empty"><span className="v2-spinner" /> Loading resumes...</div>
          ) : filtered.length === 0 ? (
            <div className="v2-empty">
              <div className="v2-empty-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              </div>
              <h3>{search ? 'No matching resumes' : 'No matching resumes'}</h3>
              <p>{search ? 'Try a different search term.' : 'Upload resumes above to build your talent pool.'}</p>
            </div>
          ) : (
            <table className="v2-table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Status</th>
                  <th>Linked Jobs</th>
                  <th>Uploaded</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const isPlaced = !!r.placedInJobId;
                  return (
                    <tr
                      key={r.resumeId}
                      id={`resume-${r.resumeId}`}
                      style={{ cursor: 'pointer', opacity: isPlaced ? 0.55 : undefined }}
                      onClick={() => navigate(`/talent/${r.resumeId}`)}
                    >
                      <td style={{ fontWeight: 500 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 380 }}>
                          <span
                            title={getResumeDisplayName(r)}
                            style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {getResumeDisplayName(r)}
                          </span>
                          {isPlaced && (
                            <span className="v2-badge" style={{ background: 'rgba(168,85,247,0.12)', color: '#a855f7', fontSize: 10, padding: '1px 6px' }}>Placed</span>
                          )}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span className={`v2-badge ${r.status === 'indexed' ? 'v2-badge-success' : 'v2-badge-warning'}`}>
                            {r.status === 'indexed' ? 'Ready' : r.status}
                          </span>
                          {shouldShowFailureDetail(r.status) && (
                            <span
                              style={{
                                fontSize: 11,
                                color: 'var(--text-muted)',
                                lineHeight: 1.25,
                                whiteSpace: 'normal',
                              }}
                            >
                              {getRejectedDetail(r)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        {(r.jdIds?.length ?? 0) === 0 ? (
                          <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>—</span>
                        ) : (
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {r.jdIds!.slice(0, 3).map(jdId => (
                              <span
                                key={jdId}
                                className="v2-badge v2-badge-primary"
                                style={{ fontSize: 10, display: 'inline-flex', alignItems: 'center', gap: 2, cursor: 'pointer', maxWidth: 180 }}
                                title="Open job workspace"
                                onClick={e => {
                                  e.stopPropagation();
                                  navigate(`/jobs/${jdId}`);
                                }}
                              >
                                <span
                                  title={jdTitles[jdId] || jdId}
                                  style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                  {jdTitles[jdId] || jdId.slice(0, 8)}
                                </span>
                              </span>
                            ))}
                            {(r.jdIds!.length > 3) && (
                              <span className="v2-badge v2-badge-muted" style={{ fontSize: 10 }}>
                                +{r.jdIds!.length - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap', color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
                        {r.uploadedAt ? <span title={timeAgo(r.uploadedAt).title}>{timeAgo(r.uploadedAt).label}</span> : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
